import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Ticket, SlidersHorizontal, X } from "lucide-react";

function BreakdownList({ title, data }) {
  const entries = Object.entries(data || {}).sort((a, b) => b[1] - a[1]).slice(0, 6);
  return (
    <div>
      <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{title}</div>
      {entries.length === 0 ? (
        <p className="text-xs text-slate-400">No data yet</p>
      ) : (
        <div className="space-y-1.5">
          {entries.map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-slate-700 truncate pr-2">{k}</span>
              <span className="text-slate-400 shrink-0">{v}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function CouponAnalytics() {
  const [data, setData] = useState(null);
  const [categorySlice, setCategorySlice] = useState("");
  const [citySlice, setCitySlice] = useState("");
  const [minUtilization, setMinUtilization] = useState(0);

  useEffect(() => {
    api.get("/admin/coupons/analytics").then(r => setData(r.data)).catch(() => {});
  }, []);

  const topCategories = useMemo(
    () => Object.entries(data?.breakdown?.category || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k]) => k),
    [data]
  );
  const topCities = useMemo(
    () => Object.entries(data?.breakdown?.city || {}).sort((a, b) => b[1] - a[1]).slice(0, 8).map(([k]) => k),
    [data]
  );

  // Slice batches: when a category/city slicer is active, recompute each
  // batch's redeemed/utilization scoped to just that slice — entirely
  // client-side, since the backend already returns a per-batch breakdown.
  const slicedBatches = useMemo(() => {
    if (!data) return [];
    return data.batch_utilization
      .map(b => {
        if (!categorySlice && !citySlice) return b;
        const catCount = categorySlice ? (b.by_category?.[categorySlice] || 0) : null;
        const cityCount = citySlice ? (b.by_city?.[citySlice] || 0) : null;
        const redeemed = categorySlice && citySlice ? Math.min(catCount, cityCount) : (catCount ?? cityCount ?? b.redeemed);
        const possible = b.redemption_limit * b.codes_count || 1;
        return { ...b, redeemed, utilization_pct: Math.round(1000 * redeemed / possible) / 10 };
      })
      .filter(b => b.utilization_pct >= minUtilization)
      .sort((a, b) => b.utilization_pct - a.utilization_pct);
  }, [data, categorySlice, citySlice, minUtilization]);

  if (!data) return null;

  const clearSlicers = () => { setCategorySlice(""); setCitySlice(""); setMinUtilization(0); };
  const slicersActive = categorySlice || citySlice || minUtilization > 0;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="coupon-analytics">
      <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Ticket className="w-4 h-4" /> Random Coupon Code Generation</h3>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{data.total_generated}</div>
          <div className="text-xs text-slate-500 mt-1">Generated</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{data.total_redeemed}</div>
          <div className="text-xs text-slate-500 mt-1">Redeemed</div>
        </div>
        <div className="bg-slate-50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-slate-900">{data.total_copied}</div>
          <div className="text-xs text-slate-500 mt-1">Copied</div>
        </div>
      </div>

      {/* ── Slicers ── */}
      <div className="border border-slate-200 rounded-lg p-4 mb-6 bg-slate-50/50">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide flex items-center gap-1.5">
            <SlidersHorizontal className="w-3.5 h-3.5" /> Slice batch utilization by
          </span>
          {slicersActive && (
            <button onClick={clearSlicers} className="text-xs text-slate-500 hover:text-black flex items-center gap-1" data-testid="clear-slicers">
              <X className="w-3 h-3" /> Clear
            </button>
          )}
        </div>

        {topCategories.length > 0 && (
          <div className="mb-3">
            <div className="text-[11px] text-slate-400 mb-1.5">Category</div>
            <div className="flex flex-wrap gap-1.5">
              {topCategories.map(c => (
                <button
                  key={c}
                  onClick={() => setCategorySlice(s => s === c ? "" : c)}
                  data-testid={`slicer-category-${c}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    categorySlice === c ? "bg-black text-white border-black" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        {topCities.length > 0 && (
          <div className="mb-3">
            <div className="text-[11px] text-slate-400 mb-1.5">City</div>
            <div className="flex flex-wrap gap-1.5">
              {topCities.map(c => (
                <button
                  key={c}
                  onClick={() => setCitySlice(s => s === c ? "" : c)}
                  data-testid={`slicer-city-${c}`}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                    citySlice === c ? "bg-black text-white border-black" : "bg-white border-slate-200 text-slate-600 hover:border-slate-400"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between text-[11px] text-slate-400 mb-1.5">
            <span>Minimum utilization</span>
            <span className="font-medium text-slate-600">{minUtilization}%+</span>
          </div>
          <input
            type="range"
            min={0}
            max={100}
            step={5}
            value={minUtilization}
            onChange={e => setMinUtilization(Number(e.target.value))}
            className="w-full accent-black"
            data-testid="slicer-min-utilization"
          />
        </div>
      </div>

      {slicedBatches.length > 0 && (
        <div className="mb-6">
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
            Batch utilization{(categorySlice || citySlice) ? ` — ${[categorySlice, citySlice].filter(Boolean).join(" · ")}` : ""} (best first)
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={slicedBatches.map(b => ({ name: `#${b.batch_number}`, utilization: b.utilization_pct }))}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="name" fontSize={11} />
              <YAxis fontSize={11} unit="%" />
              <Tooltip formatter={(v) => `${v}%`} />
              <Bar dataKey="utilization" fill="#000" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      {data.batch_utilization.length > 0 && slicedBatches.length === 0 && (
        <p className="text-xs text-slate-400 mb-6">No batches match the current slicers.</p>
      )}

      <div className="grid sm:grid-cols-3 gap-6">
        <BreakdownList title="By Category" data={data.breakdown?.category} />
        <BreakdownList title="By City" data={data.breakdown?.city} />
        <BreakdownList title="By Education" data={data.breakdown?.education} />
      </div>
    </div>
  );
}
